import { CHEST_SLOTS, CLASS_IDS, classColor, classOf, SHAPE_GLYPHS } from "@eldrun/content";
import type { InventoryType, ItemStackType } from "@eldrun/protocol";
import { INVENTORY_SLOTS } from "@eldrun/sim";
import type { WebGLRenderer } from "three";
import type { InventoryActions } from "../net/inventory-actions.ts";
import { ItemIcons } from "../render/item-icons.ts";
import type { ModelLibrary } from "../render/model-library.ts";
import { gameColor } from "../render/style/roles.ts";
import { BossBarsHud } from "../ui/hud/boss-bars.ts";
import { PartyFrames } from "../ui/hud/party-frames.ts";
import { statusList } from "../ui/hud/status-glyphs.ts";
import { RelicHud, relicSlots } from "../ui/hud/relic-hud.ts";
import { SimpatiasHud } from "../ui/hud/simpatias-hud.ts";
import { VitalsHud } from "../ui/hud/vitals-hud.ts";
import { craftingTabs } from "../ui/panels/crafting-model.ts";
import { CraftingPanel } from "../ui/panels/crafting-panel.ts";
import { abilitiesView } from "../ui/panels/abilities-model.ts";
import { AbilitiesPanel } from "../ui/panels/abilities-panel.ts";
import { BeltHud, InventoryPanel } from "../ui/panels/inventory-panel.ts";
import { applyTheme } from "../ui/kit/kit-model.ts";
import { PauseMenu } from "../ui/panels/pause-menu.ts";
import { pauseNote } from "../ui/panels/pause-model.ts";
import { sharedSettingsPanel } from "../ui/panels/settings-panel.ts";
import { startHudSample } from "./hud-showcase.ts";
import { startKitShowcase } from "./kit-showcase.ts";
import { showcaseLobby, showcaseMap, showcaseSheet } from "./panels-showcase.ts";

export interface UiShowcaseOptions {
  /** Slot do jogador (0 a 3) cuja cor é o acento; −1, o tema neutro. */
  slot: number;
  /**
   * Qual painel aberto: o inventário, a fabricação, as habilidades, a página do kit (F79), a pausa ou a pausa
   * com as Opções (F80), o mapa, a ficha ou o lobby (F82); `hud`, nenhum (o HUD da partida inteiro, F81).
   */
  panel: "inventario" | "fabricacao" | "habilidades" | "kit" | "pausa" | "opcoes" | "mapa" | "ficha" | "lobby" | "hud";
  /** Estação aberta junto do inventário (F82): um Forno aceso ou um baú. */
  station?: "forno" | "bau" | undefined;
}

/** Inventário de exemplo (sem servidor): o que o painel e o cinto leem do schema. */
function sampleInventory(): { inventory: InventoryType; stacks: { itemId: string; qty: number }[] } {
  const ids = ["machado-pedra", "picareta-pedra", "lanca-ferro", "arco-prata", "flecha-ferro", "carne-assada", "madeira", "pedra", "corda", "palha", "minerio-ferro", "lingote-ferro", "bancada"];
  const slots = Array.from({ length: INVENTORY_SLOTS }, (_, i) => ({ itemId: ids[i] ?? "", qty: ids[i] ? [1, 1, 1, 1, 24, 3, 40, 32, 6, 12, 5, 2, 1][i]! : 0 }));
  const empty = { itemId: "", qty: 0 };
  const inventory = { slots, armor: [{ itemId: "capacete-ferro", qty: 1 }, empty, empty, empty], quiver: { itemId: "flecha-ferro", qty: 18 }, selected: 2, coins: 140, simpatias: new Map([["figa", 1], ["guarana", 3], ["trovoada", 1]]) } as unknown as InventoryType;
  return { inventory, stacks: slots };
}

/** Forno aceso (madeira no fogo, minério entrando, lingote saindo) ou baú com algumas pilhas. */
function sampleStation(kind: "forno" | "bau") {
  const stack = (itemId = "", qty = 0) => ({ itemId, qty }) as ItemStackType;
  const slots = kind === "forno" ? [stack("madeira", 6), stack("minerio-ferro", 3), stack("lingote-ferro", 2)] : Array.from({ length: CHEST_SLOTS }, (_, i) => (i < 4 ? stack(["madeira", "pedra", "corda", "banana"][i], [20, 12, 3, 5][i]) : stack()));
  return { kind, slots, burn: 8, burnMax: 12, active: kind === "forno", recipe: kind === "forno" ? "lingote-ferro" : "", progress: 4, duration: 10, rate: 1 };
}

/**
 * Vitrine da UI (`?debug=ui&slot=0..3|-1&painel=inventario|fabricacao|habilidades|kit|pausa|opcoes|mapa|ficha|lobby|hud`,
 * F62, sem servidor): o HUD da partida (F81: objetivos, bússola, minimapa, botões do canto, aviso, vitais,
 * cinto, interagir, Relicário, Bênçãos, grupo, chefe) e um painel aberto, com dados de exemplo, na malha neon
 * e na cor de slot pedida. `painel=kit` (F79) mostra só os componentes do kit, no tema neutro e no do slot;
 * `pausa` e `opcoes` (F80), o menu de pausa sobre o HUD, sozinho ou com as Opções ao lado (o ui-h); `mapa`,
 * `ficha` e `lobby`, os painéis da F82.
 */
export function startUiShowcase(renderer: WebGLRenderer, library: ModelLibrary, options: UiShowcaseOptions): { stats(): Record<string, unknown>; ready?: Promise<void> } {
  if (options.panel === "kit") {
    startKitShowcase(document.body, options.slot);
    return { stats: () => ({ slot: options.slot, panel: options.panel }) };
  }
  if (options.panel === "lobby") {
    // Lobby (F82; F85 com a escolha 3D): o acento é o da classe escolhida.
    applyTheme(document.body, gameColor(classColor(CLASS_IDS[Math.max(0, options.slot)] ?? "")));
    showcaseLobby(document.getElementById("ui") ?? document.body, Math.max(0, options.slot));
    return { stats: () => ({ slot: options.slot, panel: options.panel }) };
  }
  document.body.classList.add("jogando");
  const accent = options.slot >= 0 ? gameColor(classColor(CLASS_IDS[options.slot] ?? "")) : undefined;
  applyTheme(document.body, accent);
  const stats = () => ({ slot: options.slot, accent: accent ?? "neutro", panel: options.panel });
  // Painéis da F82 que cobrem a tela: só o painel, como nos mockups (o lobby no tema neutro). São da partida:
  // a arte dos painéis da F110 vale com `hud-partida`.
  if (options.panel === "mapa" || options.panel === "ficha") document.body.classList.add("hud-partida");
  if (options.panel === "mapa") return { stats, ready: showcaseMap(document.body, Math.max(0, options.slot)) };
  if (options.panel === "ficha") {
    showcaseSheet(document.body, Math.max(0, options.slot));
    return { stats };
  }
  const icons = new ItemIcons(renderer, library);
  const { inventory, stacks } = sampleInventory();
  const actions = { move() {}, split() {}, quick() {}, discard() {}, gather() {}, select() {}, craft() {} } as unknown as InventoryActions;
  const hud = startHudSample(options.slot);
  // O cartão do jogador e as Bênçãos no alto da coluna da esquerda (F109), como na partida.
  const vitals = new VitalsHud(document.body);
  hud.left.prepend(vitals.element);
  const me = classOf(CLASS_IDS[Math.max(0, options.slot)] ?? "");
  vitals.setIdentity(SHAPE_GLYPHS[me.shape], me.name, Math.max(0, options.slot) + 1);
  vitals.update({ hp: 0.82, patua: 1, hunger: 0.97, stamina: 0.64, hpText: "82 +25", hungerText: "97", exhausted: false, patuaBroken: false, submerged: false, drowning: false, eating: 0, effect: undefined, lowHp: false, criticalHp: false, lowHunger: false, starving: false, dead: false, deathMessage: "" });
  const belt = new BeltHud(document.body, icons);
  belt.update(inventory);
  const relic = new RelicHud(hud.right);
  relic.update(relicSlots((c) => c === "verde" || c === "rosa"));
  const blessings = new SimpatiasHud(hud.left);
  blessings.update((inventory as unknown as { simpatias: Map<string, number> }).simpatias.entries());
  // Grupo e chefe de exemplo (F63; status, o que acaba e o Exposto: F76).
  const party = new PartyFrames(hud.left);
  party.update(
    [1, 2, 3].map((slot, k) => ({ id: `p${slot}`, name: ["Bia", "Caio", "Duda"][k]!, n: slot + 1, className: ["Lanceiro", "Arcanista", "Patrulheiro"][k]!, classGlyph: ["△", "◯", "≡"][k]!, statuses: k === 0 ? statusList(0b100001, 0b100000) : [], statusColor: gameColor(classColor(CLASS_IDS[0])), color: gameColor(classColor(CLASS_IDS[slot] ?? "")), hp: [0.9, 0.35, 0][k]!, patua: [0.25, 0, 0][k]!, dead: k === 2, ultReady: k === 0, connected: true, distance: [12, 40, 7][k] })),
  );
  const bosses = new BossBarsHud(document.body);
  bosses.update([{ id: "chefe", name: "Ciclope", fraction: 0.62, tag: "fase 2", exposed: true }], undefined);
  bosses.cast("chefe", "Pancada dupla", 60_000);
  if (options.panel === "pausa" || options.panel === "opcoes") {
    // Pausa (F80) com três aliados; com `opcoes`, as Opções abertas por ela, ao lado.
    const pause = new PauseMenu(document.body, { resume: () => pause.hide(), settings: () => (pause.setSettingsOpen(true), sharedSettingsPanel().show()), leave: () => {} });
    pause.show(pauseNote(3));
    if (options.panel === "opcoes") {
      pause.setSettingsOpen(true);
      sharedSettingsPanel().show();
    }
  } else if (options.panel === "habilidades") {
    // Bênçãos e Habilidades (F70), com um juramento feito e a oferta aberta.
    const abilities = new AbilitiesPanel(document.body, () => {});
    abilities.toggle(true);
    abilities.update(abilitiesView("baluarte", (inventory as unknown as { simpatias: Map<string, number> }).simpatias.entries(), "brado-amplo", ["bastiao-longo", "brado-firme"], { ativa: "botão direito", ultimate: "V" }));
  } else if (options.panel === "fabricacao") {
    const crafting = new CraftingPanel(document.body, actions, icons);
    crafting.toggle(true);
    crafting.update(craftingTabs(stacks, () => true, new Set(["bancada"])));
    // A aba da Bancada e uma receita de três ingredientes (F82: o 2 × 2 → resultado com "tem / precisa").
    crafting.element.querySelector<HTMLElement>('[data-station="bancada"]')?.click();
    crafting.element.querySelector<HTMLElement>('button[data-recipe="machado-pedra"]')?.click();
  } else if (options.panel !== "hud") {
    const panel = new InventoryPanel(document.body, actions, icons);
    panel.toggle(true);
    panel.update(inventory);
    if (options.station) panel.updateStation(sampleStation(options.station), () => true, performance.now(), options.station, "Baú de Madeira");
  }
  return { stats: () => ({ slot: options.slot, accent: accent ?? "neutro", panel: options.panel }) };
}
